import { navigate } from '../auth/router';

export type DevelopersView = 'fronts' | 'inbox' | 'analytics' | 'world' | 'fleet' | 'missions' | 'memory' | 'sessions';
export interface DevelopersRoute {
  view: DevelopersView;
  projectId?: string;
  frontId?: string;
  frontTab?: 'overview' | 'workflow' | 'preview' | 'activity';
  projectLayout?: 'fronts' | 'projects';
  projectTab?: 'overview' | 'fronts' | 'preview' | 'activity';
  agentId?: string;
  taskId?: string;
  runId?: string;
  invalid?: boolean;
  analyticsScope?: { projectId?: string; frontId?: string };
}

export function developersBase(path: string): string | null {
  if (path === '/developers' || path.startsWith('/developers/')) return '/developers';
  if (process.env.NODE_ENV === 'development' && (path === '/developers-preview' || path.startsWith('/developers-preview/'))) return '/developers-preview';
  return null;
}

export function readDevelopersRoute(path: string): DevelopersRoute | null {
  const [pathname, query = ''] = path.split('?');
  const base = developersBase(pathname);
  if (!base) return null;
  const invalid: DevelopersRoute = { view: 'fronts', invalid: true };
  let parts: string[];
  try { parts = pathname.slice(base.length).split('/').filter(Boolean).map(decodeURIComponent); } catch { return invalid; }
  if (parts.some(part => !/^[^/?#]{1,200}$/.test(part) || Array.from(part).some(character => character.charCodeAt(0) < 32))) return invalid;
  const [page, id, section, item, extra] = parts;
  const runId = new URLSearchParams(query).get('run');
  const workflowSelection = (tab: string | undefined) => tab === 'workflow' && runId ? { runId } : {};
  if (!page) return { view: 'fronts', projectLayout: 'projects' };
  if (page === 'projects' && parts.length === 1) return { view: 'fronts', projectLayout: 'projects' };
  if (page === 'projects' && id === 'work' && parts.length === 2) return { view: 'fronts', projectLayout: 'fronts' };
  if (page === 'projects' && id === 'catalog' && parts.length <= 3) return { view: 'fronts', projectLayout: 'projects', projectId: section };
  if (page === 'projects' && id && id !== 'catalog' && parts.length === 3 && ['overview', 'fronts', 'preview', 'activity'].includes(section)) return { view: 'fronts', projectId: id, projectTab: section as DevelopersRoute['projectTab'] };
  if (page === 'projects' && parts.length <= 2) return id === 'catalog' ? { view: 'fronts', projectLayout: 'projects' } : { view: 'fronts', projectId: id };
  if (page === 'fronts' && id && parts.length <= 3 && (!section || ['overview', 'workflow', 'preview', 'activity'].includes(section))) return { view: 'fronts', frontId: id, frontTab: (section || 'overview') as DevelopersRoute['frontTab'], ...workflowSelection(section) };
  if (page === 'inbox') {
    if (parts.length === 1) return { view: 'inbox' };
    if (id === 'fronts' && section && parts.length <= 4 && (!item || ['overview', 'workflow', 'preview', 'activity'].includes(item))) return { view: 'inbox', frontId: section, frontTab: (item || 'overview') as DevelopersRoute['frontTab'], ...workflowSelection(item) };
    if (id === 'projects' && section && parts.length === 3) return { view: 'inbox', projectId: section };
  }
  if (page === 'workspace' && parts.length <= 4 && (!section || (id && section === 'tasks' && item))) return { view: 'sessions', agentId: id, taskId: item };
  if ((page === 'memory' || page === 'world') && parts.length <= 2) return { view: page, agentId: id };
  if (parts.length === 1 && !extra) {
    if (page === 'analytics') return { view: 'analytics' };
    if (page === 'agents') return { view: 'fleet' };
    if (page === 'tasks') return { view: 'missions' };
  }
  return invalid;
}

export function developersHref(route: DevelopersRoute, base = '/developers'): string {
  const id = (value: string) => encodeURIComponent(value);
  if (route.view === 'analytics' && route.analyticsScope) {
    const params = new URLSearchParams();
    if (route.analyticsScope.projectId) params.set('project', route.analyticsScope.projectId);
    if (route.analyticsScope.frontId) params.set('front', route.analyticsScope.frontId);
    return `${base}/analytics${params.toString() ? `?${params.toString()}` : ''}`;
  }
  if (route.view === 'fronts' || route.view === 'inbox') {
    if (route.frontId) return `${base}/${route.view === 'inbox' ? 'inbox/fronts' : 'fronts'}/${id(route.frontId)}/${route.frontTab || 'overview'}${route.frontTab === 'workflow' && route.runId ? `?run=${id(route.runId)}` : ''}`;
    if (route.view === 'fronts' && route.projectLayout === 'projects') return `${base}/projects${route.projectId ? `/catalog/${id(route.projectId)}` : ''}`;
    if (route.projectId) return `${base}/${route.view === 'inbox' ? 'inbox/projects' : 'projects'}/${id(route.projectId)}${route.view === 'fronts' && route.projectTab ? `/${route.projectTab}` : ''}`;
    return `${base}/${route.view === 'inbox' ? 'inbox' : route.projectLayout === 'fronts' ? 'projects/work' : 'projects'}`;
  }
  if (route.view === 'sessions') return `${base}/workspace${route.agentId ? `/${id(route.agentId)}${route.taskId ? `/tasks/${id(route.taskId)}` : ''}` : ''}`;
  if (route.view === 'memory' || route.view === 'world') return `${base}/${route.view}${route.agentId ? `/${id(route.agentId)}` : ''}`;
  return `${base}/${route.view === 'fleet' ? 'agents' : route.view === 'missions' ? 'tasks' : route.view}`;
}

export function navigateDevelopers(route: DevelopersRoute, replace = false): void {
  const to = developersHref(route, developersBase(window.location.pathname) || '/developers');
  if (window.location.pathname + window.location.search === to) return;
  if (!replace) { navigate(to); return; }
  window.history.replaceState(window.history.state, '', to);
  window.dispatchEvent(new Event('lato:navigate'));
}
